import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useFamily } from '../../contexts/FamilyContext'
import { useAuth } from '../../contexts/AuthContext'
import { useCelebrationContext } from '../../contexts/CelebrationContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import toast from 'react-hot-toast'

const { FiCheck, FiStar, FiClock, FiUsers, FiZap, FiAward, FiX } = FiIcons

const TaskCompletionModal = ({ task, child, isOpen, onClose }) => {
  const { family } = useAuth()
  const { completeTask } = useFamily()
  const { celebrateTaskComplete } = useCelebrationContext()
  const [loading, setLoading] = useState(false)
  const [completionData, setCompletionData] = useState({
    qualityRating: null,
    completionTimeSeconds: null,
    isHelpingSibling: false
  })
  const [startTime] = useState(Date.now())
  const [pointsPreview, setPointsPreview] = useState(null)

  const gamificationSettings = family?.gamification_settings || {}
  const enhancedPointsEnabled = gamificationSettings.enhanced_points?.enabled
  const pointMultipliersEnabled = gamificationSettings.point_multipliers?.enabled

  useEffect(() => {
    if (isOpen) {
      calculatePointsPreview()
    }
  }, [completionData, isOpen])

  const calculatePointsPreview = async () => {
    if (!enhancedPointsEnabled && !pointMultipliersEnabled) {
      setPointsPreview({ final_points: task.points, bonus_reasons: [] })
      return
    }

    try {
      // Simulate the enhanced points calculation
      let basePoints = task.points
      let bonusPoints = 0
      let multiplier = 1.0
      let bonusReasons = []

      // Quality Bonus
      if (enhancedPointsEnabled && 
          gamificationSettings.enhanced_points?.quality_bonus?.enabled && 
          completionData.qualityRating >= 4) {
        const qualityMultiplier = gamificationSettings.enhanced_points.quality_bonus.multiplier || 1.5
        bonusPoints += Math.round(basePoints * qualityMultiplier - basePoints)
        bonusReasons.push('Quality Bonus')
      }

      // Speed Bonus
      if (enhancedPointsEnabled && 
          gamificationSettings.enhanced_points?.speed_bonus?.enabled && 
          completionData.completionTimeSeconds && 
          task.estimated_time_minutes) {
        const estimatedSeconds = task.estimated_time_minutes * 60
        if (completionData.completionTimeSeconds < estimatedSeconds * 0.8) {
          bonusPoints += gamificationSettings.enhanced_points.speed_bonus.bonus_points || 5
          bonusReasons.push('Speed Bonus')
        }
      }

      // Helper Bonus
      if (enhancedPointsEnabled && 
          gamificationSettings.enhanced_points?.helper_bonus?.enabled && 
          completionData.isHelpingSibling) {
        bonusPoints += gamificationSettings.enhanced_points.helper_bonus.bonus_points || 10
        bonusReasons.push('Helper Bonus')
      }

      // Time-based multipliers
      if (pointMultipliersEnabled) {
        const now = new Date()
        const currentTime = now.toTimeString().slice(0, 5)
        const currentDay = now.getDay()

        // Happy Hour
        if (gamificationSettings.point_multipliers?.happy_hour?.enabled) {
          const happyHour = gamificationSettings.point_multipliers.happy_hour
          if (happyHour.days?.includes(currentDay) && 
              currentTime >= happyHour.start_time && 
              currentTime <= happyHour.end_time) {
            multiplier = Math.max(multiplier, happyHour.multiplier || 1.5)
            bonusReasons.push('Happy Hour')
          }
        }

        // Weekend Warriors
        if (gamificationSettings.point_multipliers?.weekend_warriors?.enabled && 
            [0, 6].includes(currentDay)) {
          const weekendWarriors = gamificationSettings.point_multipliers.weekend_warriors
          if (weekendWarriors.categories?.includes(task.category)) {
            multiplier = Math.max(multiplier, weekendWarriors.multiplier || 2.0)
            bonusReasons.push('Weekend Warrior')
          }
        }

        // Challenge Week
        if (gamificationSettings.point_multipliers?.challenge_week?.enabled) {
          const challengeWeek = gamificationSettings.point_multipliers.challenge_week
          const today = now.toISOString().split('T')[0]
          if (challengeWeek.start_date && challengeWeek.end_date &&
              today >= challengeWeek.start_date && today <= challengeWeek.end_date) {
            multiplier = Math.max(multiplier, challengeWeek.multiplier || 2.5)
            bonusReasons.push('Challenge Week')
          }
        }
      }

      const finalPoints = Math.round((basePoints + bonusPoints) * multiplier)

      setPointsPreview({
        base_points: basePoints,
        bonus_points: bonusPoints,
        multiplier,
        final_points: finalPoints,
        bonus_reasons: bonusReasons
      })

    } catch (error) {
      console.error('Error calculating points preview:', error)
      setPointsPreview({ final_points: task.points, bonus_reasons: [] })
    }
  }

  const handleComplete = async () => {
    setLoading(true)
    try {
      const completionTimeSeconds = Math.floor((Date.now() - startTime) / 1000)
      
      // Prepare enhanced data for the completion
      const enhancedData = {
        ...completionData,
        completionTimeSeconds
      }

      // Use the completeTask function from FamilyContext with enhanced data
      const result = await completeTask(task.id, child.id, enhancedData)

      if (result && !result.error) {
        const finalPoints = pointsPreview?.final_points || task.points
        celebrateTaskComplete(child.avatar || '👦', task.title, finalPoints)
        
        if (pointsPreview?.bonus_reasons?.length > 0) {
          toast.success(`🎉 Bonus earned: ${pointsPreview.bonus_reasons.join(', ')}!`)
        }
        
        onClose()
      }
    } catch (error) {
      console.error('Error completing task:', error)
      toast.error('Failed to complete task')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <Card className="border-0 shadow-none">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Complete Task
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="p-2"
            >
              <SafeIcon icon={FiX} className="text-gray-500" />
            </Button>
          </div>

          {/* Task Info */}
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl">{task.category === 'cleaning' ? '🧹' : '⭐'}</span>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100">{task.title}</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {child?.name} • {task.points} base points
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced Points Options */}
          {(enhancedPointsEnabled || pointMultipliersEnabled) && (
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <SafeIcon icon={FiZap} className="text-primary-500" />
                <h4 className="font-medium text-gray-900 dark:text-gray-100">Bonus Opportunities</h4>
              </div>

              {/* Quality Rating */}
              {enhancedPointsEnabled && gamificationSettings.enhanced_points?.quality_bonus?.enabled && (
                <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Quality Rating (4-5 stars for bonus)
                  </label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        onClick={() => setCompletionData(prev => ({ ...prev, qualityRating: rating }))}
                        className={`p-1 rounded ${
                          completionData.qualityRating >= rating
                            ? 'text-yellow-500'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      >
                        <SafeIcon icon={FiStar} className="text-lg" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Helper Bonus */}
              {enhancedPointsEnabled && gamificationSettings.enhanced_points?.helper_bonus?.enabled && (
                <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={completionData.isHelpingSibling}
                      onChange={(e) => setCompletionData(prev => ({ 
                        ...prev, 
                        isHelpingSibling: e.target.checked 
                      }))}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      I helped a sibling with this task
                    </span>
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    +{gamificationSettings.enhanced_points.helper_bonus.bonus_points || 10} bonus points
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Points Preview */}
          {pointsPreview && (
            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <SafeIcon icon={FiAward} className="text-green-600 dark:text-green-400" />
                <h4 className="font-medium text-green-800 dark:text-green-200">Points Earned</h4>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Base Points:</span>
                  <span className="font-medium">{pointsPreview.base_points || task.points}</span>
                </div>
                
                {pointsPreview.bonus_points > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Bonus Points:</span>
                    <span className="font-medium text-green-600">+{pointsPreview.bonus_points}</span>
                  </div>
                )}
                
                {pointsPreview.multiplier > 1 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Multiplier:</span>
                    <span className="font-medium text-blue-600">{pointsPreview.multiplier}x</span>
                  </div>
                )}
                
                <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">Total Points:</span>
                    <span className="font-bold text-lg text-primary-600 dark:text-primary-400">
                      {pointsPreview.final_points}
                    </span>
                  </div>
                </div>

                {pointsPreview.bonus_reasons?.length > 0 && (
                  <div className="pt-2">
                    <div className="flex flex-wrap gap-1">
                      {pointsPreview.bonus_reasons.map((reason, index) => (
                        <Badge key={index} variant="success" size="sm">
                          {reason}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleComplete}
              loading={loading}
              className="flex-1 flex items-center gap-2"
            >
              <SafeIcon icon={FiCheck} className="text-sm" />
              Complete Task
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  )
}

export default TaskCompletionModal